import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Edit2, X, Check, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });
const TAG_OPTIONS = [
  { value: "NEET", color: "#166534" },
  { value: "JEE", color: "#1e40af" },
  { value: "MHT-CET", color: "#9a3412" },
  { value: "HSC Board", color: "#0A2540" },
];
const emptyForm = { name: "", score: "", exam: "", tag: "NEET", tag_color: "#166534", initials: "", image_url: "" };

export default function AdminToppers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/toppers`);
      setItems(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, score: item.score, exam: item.exam, tag: item.tag, tag_color: item.tag_color, initials: item.initials, image_url: item.image_url || "" });
    setShowModal(true);
  };

  const handleTagChange = (tag) => {
    const opt = TAG_OPTIONS.find((t) => t.value === tag);
    setForm({ ...form, tag, tag_color: opt ? opt.color : "#0A2540" });
  };

  const autoInitials = (name) => name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  const handleSave = async () => {
    if (!form.name || !form.score || !form.exam) { alert("Name, Score, and Exam are required."); return; }
    setSaving(true);
    const data = { ...form, initials: form.initials || autoInitials(form.name) };
    try {
      if (editItem) {
        const res = await axios.put(`${BACKEND_URL}/api/admin/toppers/${editItem.id}`, data, { headers: getHeaders() });
        setItems((prev) => prev.map((i) => (i.id === editItem.id ? res.data : i)));
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/admin/toppers`, data, { headers: getHeaders() });
        setItems((prev) => [...prev, res.data]);
      }
      setShowModal(false);
    } catch { alert("Failed to save."); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this topper?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/toppers/${id}`, { headers: getHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch { alert("Failed to delete."); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div data-testid="admin-toppers-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Toppers Management</h2>
          <p className="text-sm text-gray-400 mt-0.5">{items.length} toppers listed on website</p>
        </div>
        <button onClick={openAdd} data-testid="topper-add-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90"
          style={{ backgroundColor: "#0A2540" }}>
          <Plus size={16} /> Add Topper
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <Trophy size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400">No toppers added yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead style={{ backgroundColor: "#f8faff" }}>
              <tr>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Student</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Score</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Exam/College</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Tag</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} className="border-t border-gray-50 hover:bg-gray-50" data-testid={`topper-row-${i}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: "#0A2540" }}>
                        {item.initials}
                      </div>
                      <span className="font-semibold" style={{ color: "#0A2540" }}>{item.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{item.score}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{item.exam}</td>
                  <td className="py-3 px-4">
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: item.tag_color }}>
                      {item.tag}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
                {editItem ? "Edit Topper" : "Add New Topper"}
              </h3>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>

            <div className="space-y-3">
              {[
                { key: "name", label: "Student Name *", placeholder: "Full name" },
                { key: "score", label: "Score/Percentile *", placeholder: "e.g. NEET 572 Marks" },
                { key: "exam", label: "Exam/College *", placeholder: "e.g. MBBS – GMC Alibag" },
                { key: "initials", label: "Initials (2 letters)", placeholder: "e.g. GC" },
                { key: "image_url", label: "Photo URL (optional)", placeholder: "https://..." },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">{label}</label>
                  <input type="text" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder={placeholder}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide">Exam Tag</label>
                <select value={form.tag} onChange={(e) => handleTagChange(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-900">
                  {TAG_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.value}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} data-testid="topper-save-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: "#0A2540" }}>
                <Check size={14} />{saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
